import {t} from "@lingui/core/macro";
import {useCallback, useEffect, useMemo} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {useGrowthBookForABTest} from "@khan/analytics-growthbook";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useGuidePreferences, useSendGuideEvent} from "@khan/guide-core";
import {RequestInfo} from "@khan/shared-core";

import useProfile from "~/data-access/use-profile.ts";

import {sharedStyles} from "./message-and-context-bubble-shared-styles.ts";

import hideIcon from "@phosphor-icons/core/regular/eye-slash.svg";
import listIcon from "@phosphor-icons/core/regular/list-magnifying-glass.svg";

interface Props {
    interactionId: string;
    dabIds?: readonly string[];
    skipEvent?: boolean;
}

/**
 * A component to toggle the display of dynamic action bubbles.
 * Will only generate DABs when rendered, dabEnabled is true, and skipEvent is false.
 * @param interactionId The interaction ID to generate DABs for.
 * @param dabEnabled Whether DABs are currently enabled via the toggle.
 * @param setDabEnabled A function to set the DAB toggle state.
 * @param dabIds An optional list of DAB IDs for aria-controls.
 * @param skipEvent Whether to skip generating DABs when the component is rendered. This is useful for cases where DABs are already generated.
 */
export const MessageDabToggle = ({interactionId, dabIds, skipEvent}: Props) => {
    const sendGuideEvent = useSendGuideEvent();
    const growthbook = useGrowthBookForABTest();
    const profileResult = useProfile();

    const {preferences, refetchPreferences, setPreferences} =
        useGuidePreferences();

    const profile =
        profileResult.status === "success" ? profileResult.profile : null;

    const dabEnabled = useMemo(() => {
        if (preferences.showDABs !== null) {
            return preferences.showDABs;
        }

        if (!profile) {
            return false;
        }

        // Only include users with KM set to english
        // from US and CA in the experiment.
        if (
            preferences.guideLanguageCode !== "en" ||
            RequestInfo.kaLocale !== "en" ||
            !["US", "CA"].includes(RequestInfo.remoteCountry)
        ) {
            return false;
        }

        if ((profile.get("userDistrictInfos") ?? []).length > 0) {
            return false;
        }

        const flagName = "dynamic-action-bubbles-default";
        return growthbook?.getFeatureValueAndEnrollInABTest(flagName, false);
    }, [
        growthbook,
        preferences.guideLanguageCode,
        preferences.showDABs,
        profile,
    ]);

    useEffect(() => {
        if (dabEnabled && !skipEvent) {
            sendGuideEvent("generate_dynamic_action_bubbles", {
                params: {interactionId},
            });
        }
    }, [dabEnabled, interactionId, sendGuideEvent, skipEvent]);

    const toggleDabEnabled = useCallback(async () => {
        const showDABs = !dabEnabled;

        await setPreferences({
            showDABs,
        });

        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideShowDynamicActionBubblesPreferenceChanged",
            eventSchemaVersion: 1,
            showDABs,
        });

        refetchPreferences();
    }, [dabEnabled, refetchPreferences, setPreferences]);

    const label = dabEnabled ? t`Hide suggestions` : t`Show suggestions`;

    return (
        <View style={{flexGrow: 1, alignContent: "center"}}>
            <Tooltip content={label} placement="right">
                <IconButton
                    aria-label={label}
                    icon={dabEnabled ? hideIcon : listIcon}
                    kind="tertiary"
                    size="small"
                    style={sharedStyles.wcagAAATargetSize}
                    onClick={toggleDabEnabled}
                    role="switch"
                    aria-checked={dabEnabled}
                    aria-controls={dabIds?.join(" ") ?? undefined}
                />
            </Tooltip>
        </View>
    );
};
